// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  JdHtmlObject,
  Vacancy,
  VacancyChangeStatus,
  VacancyStatusUpdate,
  VacancyState,
  ApplicationReviewer,
  ApplicationReviewerCreate,
  ApplicationReviewerDelete,
  ReferralVacancyResponse,
} from "./model";
import { RequestState } from "@utils/types";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { VacancyCreate, VacancyEdit } from "./model";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { SnackMessage } from "@config/constant";

export const getVacancies = createAsyncThunk(
  "vacancy/getVacancies",
  async ({
    status,
    limit,
    offset,
    searchString,
    evaluator,
  }: {
    status: string[] | null;
    limit?: number;
    offset?: number;
    searchString?: string;
    evaluator?: string;
  }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.vacancies, {
          params: {
            status: status?.join(","),
            limit,
            offset,
            searchString,
            evaluator,
          },
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const getVacancy = createAsyncThunk(
  "vacancy/getVacancy",
  async (id: number) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.vacancies}/${id}`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const addVacancies = createAsyncThunk(
  "vacancy/addVacancies",
  async ({ payload }: { payload: VacancyCreate }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(AppConfig.serviceUrls.vacancies, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addVacancy,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.addVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const editVacancies = createAsyncThunk(
  "vacancy/editVacancies",
  async (
    { id, payload }: { id: number; payload: VacancyEdit },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .patch(`${AppConfig.serviceUrls.vacancies}/${id}`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateVacancy,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateVacancyStatusInReview = createAsyncThunk(
  "vacancy/updateVacancyStatusInReview",
  async ({ id }: { id: number }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.vacancies}/${id}/review`)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateVacancy,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateVacancyStatusRequestChange = createAsyncThunk(
  "vacancy/updateVacancyStatusRequestChange",
  async (
    { id, payload }: { id: number; payload: VacancyChangeStatus },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(
          `${AppConfig.serviceUrls.vacancies}/${id}/request-change`,
          payload,
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: "Request is submitted successfully",
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateVacancyStatusApprove = createAsyncThunk(
  "vacancy/updateVacancyStatusApprove",
  async (
    { id, payload }: { id: number; payload: JdHtmlObject },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.vacancies}/${id}/approve`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateVacancy,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateVacancyStatusPublish = createAsyncThunk(
  "vacancy/updateVacancyStatusPublish",
  async ({ id }: { id: number }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.vacancies}/${id}/publish`)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.vacancyPublished,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateVacancyStatusReject = createAsyncThunk(
  "vacancy/updateVacancyStatusReject",
  async (
    { id, payload }: { id: number; payload: VacancyChangeStatus },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.vacancies}/${id}/reject`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateVacancy,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateVacancyStatus = createAsyncThunk(
  "vacancy/updateVacancyStatus",
  async (payload: VacancyStatusUpdate, { dispatch, rejectWithValue }) => {
    try {
      const response = await APIService.getInstance().put(
        `${AppConfig.serviceUrls.vacancies}/status`,
        payload,
      );

      const statusMessage =
        payload.status === "DELETED"
          ? SnackMessage.success.deleteVacancy
          : SnackMessage.success.closeVacancy;

      dispatch(
        enqueueSnackbarMessage({
          message: statusMessage,
          type: "success",
        }),
      );

      return response.data;
    } catch (error: any) {
      const statusMessage =
        payload.status === "DELETED"
          ? SnackMessage.error.deleteVacancy
          : SnackMessage.error.closeVacancy;

      dispatch(
        enqueueSnackbarMessage({
          message: statusMessage,
          type: "error",
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const sendJdNotification = createAsyncThunk(
  "vacancy/sendJdNotification",
  async (
    {
      id,
      notificationType,
    }: { id: number; notificationType: "ChangeRequest" | "Comment" },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(
          `${AppConfig.serviceUrls.vacancies}/${id}/jd-update-notification`,
          {
            notificationType,
          },
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.sendJdNotificationEmail,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.sendJdNotificationEmail,
              type: "error",
            }),
          );
        });
    });
  },
);

export const syncResumeDirectory = createAsyncThunk(
  "vacancy/syncResumeDirectory",
  async (_, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.vacancies}/resume-url/sync`)
        .then((response) => {
          dispatch(
            enqueueSnackbarMessage({
              message: response.data?.status ?? "Sync started successfully.",
              type: "success",
            }),
          );
          resolve(response.data);
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data ??
            "Failed to start vacancy resume directory sync.";
          dispatch(
            enqueueSnackbarMessage({
              message:
                typeof errorMessage === "string"
                  ? errorMessage
                  : "Failed to start vacancy resume directory sync.",
              type: "error",
            }),
          );
          reject(error);
        });
    });
  },
);

export const checkSyncResumeDirectoryStatus = createAsyncThunk(
  "vacancy/checkSyncResumeDirectoryStatus",
  async (_, { dispatch }) => {
    return new Promise<{ state: string }>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.vacancies}/resume-url/sync/status`)
        .then((response) => {
          if (response.data?.value) {
            resolve({ state: response.data.value as string });
          } else {
            reject("Invalid sync resume directory status response");
            dispatch(
              enqueueSnackbarMessage({
                message: "Unable to read sync resume directory status.",
                type: "error",
              }),
            );
          }
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data ||
            "Unable to read sync resume directory status.";
          dispatch(
            enqueueSnackbarMessage({
              message:
                typeof errorMessage === "string"
                  ? errorMessage
                  : "Unable to read sync resume directory status.",
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateVacancyStatusOnHold = createAsyncThunk(
  "vacancy/updateVacancyStatusOnHold",
  async ({ id }: { id: number }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.vacancies}/${id}/on-hold`)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.vacancyPutOnHold,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateVacancyStatusRepublish = createAsyncThunk(
  "vacancy/updateVacancyStatusRepublish",
  async ({ id }: { id: number }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.vacancies}/${id}/republish`)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.vacancyRepublished,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getApplicationReviewers = createAsyncThunk(
  "vacancy/getApplicationReviewers",
  async (vacancyId: number, { dispatch }) => {
    return new Promise<ApplicationReviewer[]>((resolve, reject) => {
      APIService.getInstance()
        .get(
          `${AppConfig.serviceUrls.vacancies}/${vacancyId}/application-reviewers`,
        )
        .then((response) => resolve(response.data))
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.fetchParticipants,
              type: "error",
            }),
          );
        });
    });
  },
);

export const addApplicationReviewers = createAsyncThunk(
  "vacancy/addApplicationReviewers",
  async (
    {
      vacancyId,
      payload,
    }: { vacancyId: number; payload: ApplicationReviewerCreate },
    { dispatch },
  ) => {
    return new Promise<number>((resolve, reject) => {
      APIService.getInstance()
        .post(
          `${AppConfig.serviceUrls.vacancies}/${vacancyId}/application-reviewers`,
          payload,
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addParticipant,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.addParticipant,
              type: "error",
            }),
          );
        });
    });
  },
);

export const deleteApplicationReviewers = createAsyncThunk(
  "vacancy/deleteApplicationReviewers",
  async (
    {
      vacancyId,
      payload,
    }: { vacancyId: number; payload: ApplicationReviewerDelete },
    { dispatch },
  ) => {
    return new Promise<number>((resolve, reject) => {
      APIService.getInstance()
        .delete(
          `${AppConfig.serviceUrls.vacancies}/${vacancyId}/application-reviewers`,
          { data: payload },
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.deleteApplicationReviewer,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.deleteApplicationReviewer,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getReferralVacancies = createAsyncThunk(
  "vacancy/getReferralVacancies",
  async (
    {
      limit,
      offset,
      searchString,
    }: {
      limit?: number;
      offset?: number;
      searchString?: string;
    },
    { dispatch },
  ) => {
    return new Promise<ReferralVacancyResponse>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.vacancies}/referrals`, {
          params: { limit, offset, searchString },
        })
        .then((response) => resolve(response.data))
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateVacancy,
              type: "error",
            }),
          );
        });
    });
  },
);

const initialState: VacancyState = {
  vacancyResponse: {
    vacancy: [],
    totalCount: 0,
  },
  referralVacancyResponse: {
    vacancies: [],
    totalCount: 0,
  },
  referralVacancyState: RequestState.IDLE,
  approvedJdHtmlObject: null,
  vacancyRequestState: RequestState.IDLE,
  state: RequestState.IDLE,
  updateState: RequestState.IDLE,
  sendJdNotificationState: RequestState.IDLE,
  syncResumeDirectoryState: RequestState.IDLE,
  applicationReviewers: [],
  applicationReviewersState: RequestState.IDLE,
  addApplicationReviewerState: RequestState.IDLE,
  deleteApplicationReviewerState: RequestState.IDLE,
};

const vacancySlice = createSlice({
  name: "vacancy",
  initialState,
  reducers: {
    resetApprovedJdHtmlObject: (state) => {
      state.approvedJdHtmlObject = null;
    },
    resetVacancy: (state) => {
      state.vacancyRequestState = RequestState.IDLE;
    },
    resetApplicationReviewers: (state) => {
      state.applicationReviewers = [];
      state.applicationReviewersState = RequestState.IDLE;
      state.addApplicationReviewerState = RequestState.IDLE;
      state.deleteApplicationReviewerState = RequestState.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getVacancies.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      getVacancies.fulfilled,
      (
        state,
        action: PayloadAction<{ vacancies: Vacancy[]; totalCount: number }>,
      ) => {
        state.vacancyResponse.vacancy = action.payload.vacancies;
        state.vacancyResponse.totalCount = action.payload.totalCount;
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getVacancies.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getVacancy.pending, (state) => {
      state.vacancyRequestState = RequestState.LOADING;
    });
    builder.addCase(
      getVacancy.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        state.approvedJdHtmlObject = action.payload.jdHtmlObject;
        state.vacancyRequestState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getVacancy.rejected, (state) => {
      state.vacancyRequestState = RequestState.FAILED;
    });

    builder.addCase(addVacancies.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      addVacancies.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        state.vacancyResponse.vacancy = [
          action.payload,
          ...state.vacancyResponse.vacancy,
        ];
        state.vacancyResponse.totalCount += 1;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(addVacancies.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(editVacancies.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      editVacancies.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        const response = action.payload;
        const updatedVacancyList = state.vacancyResponse.vacancy.map(
          (vacancy) => {
            if (response.id === vacancy.id) {
              return response;
            }
            return vacancy;
          },
        );
        state.vacancyResponse.vacancy = updatedVacancyList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(editVacancies.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateVacancyStatusInReview.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateVacancyStatusInReview.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        const response = action.payload;
        const updatedVacancyList = state.vacancyResponse.vacancy.map(
          (vacancy) => {
            if (response.id === vacancy.id) {
              return response;
            }
            return vacancy;
          },
        );
        state.vacancyResponse.vacancy = updatedVacancyList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateVacancyStatusInReview.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateVacancyStatusRequestChange.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateVacancyStatusRequestChange.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        const response = action.payload;
        const updatedVacancyList = state.vacancyResponse.vacancy.map(
          (vacancy) => {
            if (response.id === vacancy.id) {
              return response;
            }
            return vacancy;
          },
        );
        state.vacancyResponse.vacancy = updatedVacancyList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateVacancyStatusRequestChange.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateVacancyStatusApprove.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateVacancyStatusApprove.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        const response = action.payload;
        const updatedVacancyList = state.vacancyResponse.vacancy.map(
          (vacancy) => {
            if (response.id === vacancy.id) {
              return response;
            }
            return vacancy;
          },
        );
        state.vacancyResponse.vacancy = updatedVacancyList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateVacancyStatusApprove.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateVacancyStatusPublish.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateVacancyStatusPublish.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        const response = action.payload;
        const updatedVacancyList = state.vacancyResponse.vacancy.map(
          (vacancy) => {
            if (response.id === vacancy.id) {
              return response;
            }
            return vacancy;
          },
        );
        state.vacancyResponse.vacancy = updatedVacancyList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateVacancyStatusPublish.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateVacancyStatusReject.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateVacancyStatusReject.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        const response = action.payload;
        const updatedVacancyList = state.vacancyResponse.vacancy.map(
          (vacancy) => {
            if (response.id === vacancy.id) {
              return response;
            }
            return vacancy;
          },
        );
        state.vacancyResponse.vacancy = updatedVacancyList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateVacancyStatusReject.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateVacancyStatus.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateVacancyStatus.fulfilled,
      (state, action: PayloadAction<number[]>) => {
        const vacancyIdArray = action.payload;
        const updatedVacancyList = state.vacancyResponse.vacancy.filter(
          (vacancy) => !vacancyIdArray.includes(vacancy.id),
        );
        state.vacancyResponse.vacancy = updatedVacancyList;
        state.vacancyResponse.totalCount -= vacancyIdArray.length;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateVacancyStatus.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(sendJdNotification.pending, (state) => {
      state.sendJdNotificationState = RequestState.LOADING;
      state.stateMessage = "Sending JD Notification Email...";
    });
    builder.addCase(sendJdNotification.fulfilled, (state) => {
      state.sendJdNotificationState = RequestState.SUCCEEDED;
    });
    builder.addCase(sendJdNotification.rejected, (state) => {
      state.sendJdNotificationState = RequestState.FAILED;
    });

    builder.addCase(syncResumeDirectory.pending, (state) => {
      state.syncResumeDirectoryState = RequestState.LOADING;
    });
    builder.addCase(syncResumeDirectory.fulfilled, (state) => {
      state.syncResumeDirectoryState = RequestState.SUCCEEDED;
    });
    builder.addCase(syncResumeDirectory.rejected, (state) => {
      state.syncResumeDirectoryState = RequestState.FAILED;
    });

    builder.addCase(checkSyncResumeDirectoryStatus.pending, (state) => {
      state.syncResumeDirectoryState = RequestState.LOADING;
    });
    builder.addCase(checkSyncResumeDirectoryStatus.fulfilled, (state) => {
      state.syncResumeDirectoryState = RequestState.SUCCEEDED;
    });
    builder.addCase(checkSyncResumeDirectoryStatus.rejected, (state) => {
      state.syncResumeDirectoryState = RequestState.FAILED;
    });

    builder.addCase(updateVacancyStatusOnHold.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateVacancyStatusOnHold.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        const response = action.payload;
        state.vacancyResponse.vacancy = state.vacancyResponse.vacancy.map(
          (vacancy) => (response.id === vacancy.id ? response : vacancy),
        );
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateVacancyStatusOnHold.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateVacancyStatusRepublish.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateVacancyStatusRepublish.fulfilled,
      (state, action: PayloadAction<Vacancy>) => {
        const response = action.payload;
        state.vacancyResponse.vacancy = state.vacancyResponse.vacancy.map(
          (vacancy) => (response.id === vacancy.id ? response : vacancy),
        );
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateVacancyStatusRepublish.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(getApplicationReviewers.pending, (state) => {
      state.applicationReviewersState = RequestState.LOADING;
    });
    builder.addCase(
      getApplicationReviewers.fulfilled,
      (state, action: PayloadAction<ApplicationReviewer[]>) => {
        state.applicationReviewers = action.payload;
        state.applicationReviewersState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getApplicationReviewers.rejected, (state) => {
      state.applicationReviewersState = RequestState.FAILED;
    });

    builder.addCase(addApplicationReviewers.pending, (state) => {
      state.addApplicationReviewerState = RequestState.LOADING;
    });
    builder.addCase(addApplicationReviewers.fulfilled, (state) => {
      state.addApplicationReviewerState = RequestState.SUCCEEDED;
    });
    builder.addCase(addApplicationReviewers.rejected, (state) => {
      state.addApplicationReviewerState = RequestState.FAILED;
    });

    builder.addCase(deleteApplicationReviewers.pending, (state) => {
      state.deleteApplicationReviewerState = RequestState.LOADING;
    });
    builder.addCase(deleteApplicationReviewers.fulfilled, (state) => {
      state.deleteApplicationReviewerState = RequestState.SUCCEEDED;
    });
    builder.addCase(deleteApplicationReviewers.rejected, (state) => {
      state.deleteApplicationReviewerState = RequestState.FAILED;
    });

    builder.addCase(getReferralVacancies.pending, (state) => {
      state.referralVacancyState = RequestState.LOADING;
    });
    builder.addCase(
      getReferralVacancies.fulfilled,
      (state, action: PayloadAction<ReferralVacancyResponse>) => {
        state.referralVacancyResponse.vacancies = action.payload.vacancies;
        state.referralVacancyResponse.totalCount = action.payload.totalCount;
        state.referralVacancyState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getReferralVacancies.rejected, (state) => {
      state.referralVacancyState = RequestState.FAILED;
    });
  },
});

export const {
  resetApprovedJdHtmlObject,
  resetVacancy,
  resetApplicationReviewers,
} = vacancySlice.actions;
export default vacancySlice.reducer;
